/**
 * Deterministic, server-authoritative stroke simulator.
 *
 * Faithfully ports the original engine: 10 substeps per frame with continuous
 * collision, speed-dependent friction, 45-degree/curved wall reflection with
 * per-surface bounciness, forgiving hole suction, water sinking, speed arrows,
 * one-way walls, magnets, mines that blow into craters, breakable walls,
 * moveable blocks, and optional ball-ball collisions. Pure + deterministic
 * given the same inputs + seed, so the client can predict strokes exactly.
 *
 * The input map is never mutated: course changes (mines, breakables,
 * moveables) are simulated on a lazy copy and reported as `mutations` so the
 * server/client can persist them onto their Track after the stroke.
 */
import { Surface, type SimBall, type PathSample, type StrokeEvent, type BallOutcome, type StrokeResult, type MapMutation, type Vec2, type Tile } from '../types.js';
import {
  SUBSTEPS,
  SUBSTEP_DT,
  MAX_SPEED,
  STOP_SPEED,
  COLLISION_CARDINAL as CC,
  COLLISION_DIAGONAL as CD,
  FIELD_MIN_X,
  FIELD_MAX_X,
  FIELD_MIN_Y,
  FIELD_MAX_Y,
  BALL_RADIUS,
  HOLE_SUCTION_ACCEL,
  HOLE_CAPTURE_MAX_SPEED,
  HOLE_SINK_NEIGHBORS,
  WATER_SETTLE_FRAMES,
  STROKE_SOFT_TIMEOUT_FRAMES,
  STROKE_HARD_TIMEOUT_FRAMES,
  MAX_SIM_FRAMES,
  BALL_COLLISION_DAMPING,
  DIAG,
  SAMPLE_INTERVAL,
  FRAME_MS,
  TILE_SIZE,
  MAP_COLS,
  MAP_ROWS,
  MAGNET_RADIUS,
  MAGNET_ACCEL,
  MAGNET_INFLUENCE_CAP,
  MUTATION_COOLDOWN_FRAMES,
} from '../constants.js';
import { CollisionMap, surfaceAt, tileCenter } from './track.js';
import {
  calculateFriction,
  isSolidFor,
  isSpeedTile,
  downhillAccel,
  wallBounciness,
  isDeadlyWater,
  isWater,
  isMine,
  isTeleportEntry,
  teleportExitFor,
  isBreakable,
  breakableNext,
  isMoveable,
} from './surfaces.js';
import { Rng } from './rng.js';

export interface SimParams {
  balls: SimBall[];
  map: CollisionMap;
  cup: Vec2;
  tee: Vec2;
  collision: boolean;
  seed: number;
  recordSamples?: boolean;
  /**
   * Optional tile grid matching `map`. When provided, destroyed breakables and
   * vacated moveable-block tiles expose their true background surface during
   * the rest of the stroke; without it the simulator assumes grass.
   */
  tiles?: Tile[];
  /**
   * Optional frame ceiling. Defaults to MAX_SIM_FRAMES so the authoritative
   * game + client prediction are unchanged; the bot passes a lower value to
   * keep its thousands of planning rollouts fast.
   */
  maxFrames?: number;
}

interface BallRuntime extends SimBall {
  inHole: boolean;
  settle: number;
  stoppedFrames: number;
  holeInfluenceFrames: number;
  magnetInfluenceFrames: number;
  startX: number;
  startY: number;
  lastDryX: number;
  lastDryY: number;
  drownedInAcid: boolean;
  teleportCooldown: boolean;
  active: boolean;
}

function speedOf(b: BallRuntime): number {
  return Math.sqrt(b.vx * b.vx + b.vy * b.vy);
}

function canCollideWithBalls(b: BallRuntime): boolean {
  return b.strokesThisHole === undefined || b.strokesThisHole > 0;
}

// ─────────────────────────────────────────────────────────────
// Static feature caches (keyed by the pristine input map).
// Magnets and teleport exits never change mid-round, so scanning once per map
// keeps bot planning (hundreds of rollouts) cheap.
// ─────────────────────────────────────────────────────────────
interface MagnetInfo {
  x: number;
  y: number;
  repel: boolean;
}

const magnetCache = new WeakMap<CollisionMap, MagnetInfo[]>();

function magnetsOf(map: CollisionMap): MagnetInfo[] {
  let list = magnetCache.get(map);
  if (list) return list;
  const seen = new Set<number>();
  list = [];
  for (let i = 0; i < map.cells.length; i++) {
    const s = map.cells[i];
    if (s !== Surface.MagnetAttract && s !== Surface.MagnetRepel) continue;
    const col = ((i % map.width) / TILE_SIZE) | 0;
    const row = (i / map.width / TILE_SIZE) | 0;
    const key = row * MAP_COLS + col;
    if (seen.has(key)) continue;
    seen.add(key);
    const c = tileCenter(col, row);
    list.push({ x: c.x, y: c.y, repel: s === Surface.MagnetRepel });
  }
  magnetCache.set(map, list);
  return list;
}

const exitCache = new WeakMap<CollisionMap, Map<Surface, Vec2[]>>();

/** All tile-centers holding a given surface (teleport exits). */
function findSurfaceAll(map: CollisionMap, surf: Surface): Vec2[] {
  let perMap = exitCache.get(map);
  if (!perMap) {
    perMap = new Map();
    exitCache.set(map, perMap);
  }
  const cached = perMap.get(surf);
  if (cached) return cached;
  const seen = new Set<number>();
  const out: Vec2[] = [];
  for (let i = 0; i < map.cells.length; i++) {
    if (map.cells[i] !== surf) continue;
    const col = ((i % map.width) / TILE_SIZE) | 0;
    const row = (i / map.width / TILE_SIZE) | 0;
    const key = row * MAP_COLS + col;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(tileCenter(col, row));
  }
  perMap.set(surf, out);
  return out;
}

export function simulateStroke(params: SimParams): StrokeResult {
  const { cup, collision, tiles } = params;
  const rng = new Rng(params.seed);
  const recordSamples = params.recordSamples !== false;

  // Copy-on-write view of the map: mutations (mines, breakables, moveables)
  // must be visible for the rest of THIS stroke without touching the caller's
  // authoritative map (which is updated from `mutations` after the stroke).
  const map: CollisionMap = { width: params.map.width, height: params.map.height, cells: params.map.cells };
  let mapOwned = false;
  const ownMap = (): void => {
    if (mapOwned) return;
    map.cells = new Uint8Array(map.cells);
    mapOwned = true;
  };

  const magnets = magnetsOf(params.map);
  const mutations: MapMutation[] = [];
  /** tileIndex -> last frame it was mutated (debounce repeated substep hits). */
  const lastMutationFrame = new Map<number, number>();

  const tileBg = (col: number, row: number): Surface => {
    const t = tiles?.[row * MAP_COLS + col];
    return t ? t.bg : Surface.Grass;
  };

  const fillTile = (col: number, row: number, fill: (current: Surface) => Surface): void => {
    ownMap();
    const x0 = col * TILE_SIZE;
    const y0 = row * TILE_SIZE;
    for (let py = 0; py < TILE_SIZE; py++) {
      const base = (y0 + py) * map.width + x0;
      for (let px = 0; px < TILE_SIZE; px++) {
        map.cells[base + px] = fill(map.cells[base + px] as Surface);
      }
    }
  };

  const balls: BallRuntime[] = params.balls.map((b) => ({
    ...b,
    inHole: false,
    settle: 0,
    stoppedFrames: 0,
    holeInfluenceFrames: 0,
    magnetInfluenceFrames: 0,
    startX: b.x,
    startY: b.y,
    lastDryX: b.x,
    lastDryY: b.y,
    drownedInAcid: false,
    teleportCooldown: false,
    active: !b.holed && (b.vx !== 0 || b.vy !== 0 || true),
  }));

  const ballIds = balls.map((b) => b.id);
  const samples: PathSample[] = [];
  const events: StrokeEvent[] = [];
  let bounciness = 1.0;
  let frame = 0;

  const cardinalNeighbors = (x: number, y: number) => ({
    n: surfaceAt(map, x, y - CC),
    e: surfaceAt(map, x + CC, y),
    s: surfaceAt(map, x, y + CC),
    w: surfaceAt(map, x - CC, y),
    ne: surfaceAt(map, x + CD, y - CD),
    se: surfaceAt(map, x + CD, y + CD),
    sw: surfaceAt(map, x - CD, y + CD),
    nw: surfaceAt(map, x - CD, y - CD),
  });

  // ── Persistent-course reactions ──────────────────────────────

  /** Mine explosion: crater the tile so it can't blow twice. */
  const blowMine = (cx: number, cy: number, big: boolean): void => {
    const col = (cx / TILE_SIZE) | 0;
    const row = (cy / TILE_SIZE) | 0;
    const to = big ? Surface.BlownBigMine : Surface.BlownMine;
    const from = big ? Surface.BigMine : Surface.Mine;
    fillTile(col, row, (s) => (s === from ? to : s));
    mutations.push({ col, row, to, frame });
  };

  /**
   * A wall was struck. Breakables crack (and finally shatter); moveable
   * blocks slide one tile along the dominant impact axis (sinking into
   * water). Debounced per tile so a single physical hit that trips several
   * substep samples only counts once.
   */
  const onWallHit = (surf: Surface, sampleX: number, sampleY: number, impactVx: number, impactVy: number): void => {
    if (!isBreakable(surf) && !isMoveable(surf)) return;
    const col = (sampleX / TILE_SIZE) | 0;
    const row = (sampleY / TILE_SIZE) | 0;
    if (col < 0 || col >= MAP_COLS || row < 0 || row >= MAP_ROWS) return;
    const key = row * MAP_COLS + col;
    const last = lastMutationFrame.get(key);
    if (last !== undefined && frame - last < MUTATION_COOLDOWN_FRAMES) return;

    if (isBreakable(surf)) {
      lastMutationFrame.set(key, frame);
      const next = breakableNext(surf);
      if (next === null) {
        const bg = tileBg(col, row);
        fillTile(col, row, () => bg);
        mutations.push({ col, row, to: null, frame });
      } else {
        fillTile(col, row, (s) => (s === surf ? next : s));
        mutations.push({ col, row, to: next, frame });
      }
      return;
    }

    // Moveable block: slide one tile along the dominant impact axis
    // (pre-reflection velocity — the direction the ball was travelling).
    const horizontal = Math.abs(impactVx) >= Math.abs(impactVy);
    const dc = horizontal ? (impactVx > 0 ? 1 : -1) : 0;
    const dr = horizontal ? 0 : impactVy > 0 ? 1 : -1;
    const nc = col + dc;
    const nr = row + dr;
    // Never slide into (or past) the outer border wall.
    if (nc < 1 || nc >= MAP_COLS - 1 || nr < 1 || nr >= MAP_ROWS - 1) return;
    // Destination must be fully passable (check center + edge midpoints).
    const cxp = nc * TILE_SIZE + (TILE_SIZE >> 1);
    const cyp = nr * TILE_SIZE + (TILE_SIZE >> 1);
    const off = TILE_SIZE >> 1;
    const dest = surfaceAt(map, cxp, cyp);
    const probes: Surface[] = [
      dest,
      surfaceAt(map, cxp - off + 1, cyp),
      surfaceAt(map, cxp + off - 1, cyp),
      surfaceAt(map, cxp, cyp - off + 1),
      surfaceAt(map, cxp, cyp + off - 1),
    ];
    if (probes.some((s) => isSolidFor(s, 0, 0) || s === Surface.Hole)) return;
    // No ball may occupy the destination tile.
    for (const o of balls) {
      if (o.inHole || o.drowned) continue;
      if (((o.x / TILE_SIZE) | 0) === nc && ((o.y / TILE_SIZE) | 0) === nr) return;
    }

    lastMutationFrame.set(key, frame);
    lastMutationFrame.set(nr * MAP_COLS + nc, frame);
    const sinks = isWater(dest);
    const blockSurf = surf;
    const bg = tileBg(col, row);
    fillTile(col, row, () => bg);
    mutations.push({ col, row, to: null, frame });
    if (sinks) {
      fillTile(nc, nr, () => Surface.SunkMoveable);
      mutations.push({ col: nc, row: nr, to: Surface.SunkMoveable, frame });
      events.push({ t: 'water', x: cxp, y: cyp, frame });
    } else {
      fillTile(nc, nr, () => blockSurf);
      mutations.push({ col: nc, row: nr, to: blockSurf, frame });
    }
  };

  const pushSample = () => {
    if (!recordSamples) return;
    const p: Array<[number, number]> = balls.map((b) => [Math.round(b.x * 100) / 100, Math.round(b.y * 100) / 100]);
    samples.push({ p, dt: FRAME_MS * SAMPLE_INTERVAL });
  };
  pushSample(); // initial positions

  const anyMoving = () => balls.some((b) => b.active && !b.inHole && !b.drowned && (Math.abs(b.vx) > 1e-6 || Math.abs(b.vy) > 1e-6));

  const frameCeil = params.maxFrames && params.maxFrames > 0 ? Math.min(params.maxFrames, MAX_SIM_FRAMES) : MAX_SIM_FRAMES;
  while (frame < frameCeil) {
    frame++;
    let stoppedCount = 0;

    for (const b of balls) {
      if (!b.active || b.inHole || b.holed) {
        stoppedCount++;
        continue;
      }
      if (b.drowned) {
        // settling underwater
        b.settle++;
        if (b.settle >= WATER_SETTLE_FRAMES) stoppedCount++;
        continue;
      }

      // ── substeps: movement + collisions ──────────────────────
      for (let s = 0; s < SUBSTEPS; s++) {
        b.x += b.vx * SUBSTEP_DT;
        b.y += b.vy * SUBSTEP_DT;

        // Outer walls (clamp + reflect so the ball never sticks).
        if (b.x < FIELD_MIN_X) {
          b.x = FIELD_MIN_X;
          if (b.vx < 0) b.vx = -b.vx * 0.81;
        } else if (b.x > FIELD_MAX_X) {
          b.x = FIELD_MAX_X;
          if (b.vx > 0) b.vx = -b.vx * 0.81;
        }
        if (b.y < FIELD_MIN_Y) {
          b.y = FIELD_MIN_Y;
          if (b.vy < 0) b.vy = -b.vy * 0.81;
        } else if (b.y > FIELD_MAX_Y) {
          b.y = FIELD_MAX_Y;
          if (b.vy > 0) b.vy = -b.vy * 0.81;
        }

        // Ball-ball collisions
        if (collision && canCollideWithBalls(b)) {
          for (const o of balls) {
            if (o === b || !o.active || o.inHole || o.drowned || !canCollideWithBalls(o)) continue;
            const dx = o.x - b.x;
            const dy = o.y - b.y;
            const d2 = dx * dx + dy * dy;
            const minD = BALL_RADIUS * 2;
            if (d2 > 0 && d2 < minD * minD) {
              resolveBallCollision(b, o);
              b.vx *= BALL_COLLISION_DAMPING;
              b.vy *= BALL_COLLISION_DAMPING;
              o.vx *= BALL_COLLISION_DAMPING;
              o.vy *= BALL_COLLISION_DAMPING;
              o.active = true;
              events.push({ t: 'collide', x: b.x, y: b.y, frame });
            }
          }
        }

        const cx = (b.x + 0.5) | 0;
        const cy = (b.y + 0.5) | 0;
        const center = surfaceAt(map, cx, cy);

        if (isDeadlyWater(center) || isWater(center)) {
          b.vx *= 0.97;
          b.vy *= 0.97;
        }

        const nb = cardinalNeighbors(cx, cy);

        // Teleport — random exit among all same-color pads (authentic).
        if (!b.teleportCooldown) {
          const entries: Surface[] = [nb.n, nb.e, nb.s, nb.w, nb.ne, nb.se, nb.sw, nb.nw];
          const entry = entries.find((s2) => isTeleportEntry(s2));
          if (entry) {
            const exit = teleportExitFor(entry);
            const dests = exit !== null ? findSurfaceAll(params.map, exit) : [];
            if (dests.length > 0) {
              const dest = dests.length === 1 ? dests[0] : dests[rng.int(0, dests.length - 1)];
              b.x = dest.x;
              b.y = dest.y;
              b.teleportCooldown = true;
              events.push({ t: 'teleport', x: dest.x, y: dest.y, frame });
            }
          }
        } else if (![nb.n, nb.e, nb.s, nb.w, nb.ne, nb.se, nb.sw, nb.nw].some(isTeleportEntry)) {
          b.teleportCooldown = false;
        }

        // Mine — blows once, then remains a crater for the rest of the hole.
        if (isMine(center)) {
          const big = center === Surface.BigMine;
          let mag = 0;
          do {
            b.vx = rng.float(-6.5, 6.5);
            b.vy = rng.float(-6.5, 6.5);
            mag = speedOf(b);
          } while (mag < 5.2 || mag > 6.5);
          if (big) {
            b.vx *= 1.2;
            b.vy *= 1.2;
          } else {
            b.vx *= 0.8;
            b.vy *= 0.8;
          }
          blowMine(cx, cy, big);
          events.push({ t: 'mine', x: cx, y: cy, frame });
        }

        // Walls
        const r = reflectWalls(b, cx, cy, nb, bounciness, onWallHit);
        if (r.hit) {
          bounciness = r.bounciness;
          events.push({ t: r.hard ? 'bounce' : 'wall', x: b.x, y: b.y, frame });
        }
      }

      // ── per-frame surface effects (after substeps) ───────────
      const cx = (b.x + 0.5) | 0;
      const cy = (b.y + 0.5) | 0;
      const center = surfaceAt(map, cx, cy);

      let onForceTile = false;

      // Speed arrows (downhill)
      if (isSpeedTile(center)) {
        const a = downhillAccel(center);
        b.vx += a.ax;
        b.vy += a.ay;
        onForceTile = true;
      }

      // Magnets: linear-falloff radial pull/push from every magnet in range.
      if (magnets.length > 0) {
        let influenced = false;
        for (const m of magnets) {
          const mdx = m.x - b.x;
          const mdy = m.y - b.y;
          const d = Math.sqrt(mdx * mdx + mdy * mdy);
          if (d >= MAGNET_RADIUS || d < 1e-3) continue;
          const a = MAGNET_ACCEL * (1 - d / MAGNET_RADIUS);
          const k = (m.repel ? -a : a) / d;
          b.vx += mdx * k;
          b.vy += mdy * k;
          influenced = true;
        }
        if (influenced) {
          b.magnetInfluenceFrames++;
          // Attract fields would otherwise keep a resting ball "live" forever;
          // after a while let it settle where it is (usually on the magnet).
          if (b.magnetInfluenceFrames <= MAGNET_INFLUENCE_CAP) onForceTile = true;
        } else {
          b.magnetInfluenceFrames = 0;
        }
      }

      // Hole suction
      const nb = cardinalNeighbors(cx, cy);
      const nearHole =
        center === Surface.Hole ||
        nb.n === Surface.Hole ||
        nb.ne === Surface.Hole ||
        nb.e === Surface.Hole ||
        nb.se === Surface.Hole ||
        nb.s === Surface.Hole ||
        nb.sw === Surface.Hole ||
        nb.w === Surface.Hole ||
        nb.nw === Surface.Hole;
      if (nearHole) {
        onForceTile = true;
        const strength = center === Surface.Hole ? 1.0 : 0.5;
        const a = HOLE_SUCTION_ACCEL * strength;
        const d = a * DIAG;
        let holeCount = 0;
        const H = Surface.Hole;
        if (nb.n === H) holeCount++; else b.vy += a;
        if (nb.ne === H) holeCount++; else { b.vy += d; b.vx -= d; }
        if (nb.e === H) holeCount++; else b.vx -= a;
        if (nb.se === H) holeCount++; else { b.vy -= d; b.vx -= d; }
        if (nb.s === H) holeCount++; else b.vy -= a;
        if (nb.sw === H) holeCount++; else { b.vy -= d; b.vx += d; }
        if (nb.w === H) holeCount++; else b.vx += a;
        if (nb.nw === H) holeCount++; else { b.vy += d; b.vx += d; }

        if (holeCount >= HOLE_SINK_NEIGHBORS && speedOf(b) <= HOLE_CAPTURE_MAX_SPEED) {
          b.inHole = true;
          b.vx = 0;
          b.vy = 0;
          b.x = cup.x;
          b.y = cup.y;
          events.push({ t: 'sink', x: cup.x, y: cup.y, frame });
        } else {
          b.holeInfluenceFrames++;
          if (b.holeInfluenceFrames > 500) onForceTile = false;
        }
      } else {
        b.holeInfluenceFrames = 0;
      }

      if (b.inHole) {
        stoppedCount++;
        continue;
      }

      // Friction (speed-dependent)
      let speed = speedOf(b);
      if (speed > 0) {
        const eff = calculateFriction(center, speed);
        b.vx *= eff;
        b.vy *= eff;
        speed *= eff;
        if (speed > MAX_SPEED) {
          const k = MAX_SPEED / speed;
          b.vx *= k;
          b.vy *= k;
          speed = MAX_SPEED;
        }
      }

      // Track last dry position (for shore water reset)
      if (!isWater(center) && !nearHole && !onForceTile) {
        b.lastDryX = b.x;
        b.lastDryY = b.y;
      }

      // Timeout safety
      if (frame > STROKE_SOFT_TIMEOUT_FRAMES) bounciness = 0;
      if (frame > STROKE_HARD_TIMEOUT_FRAMES) {
        b.vx = 0;
        b.vy = 0;
        speed = 0;
      }

      // Stop / drown
      if (speed < STOP_SPEED && !onForceTile) {
        b.vx = 0;
        b.vy = 0;
        if (isWater(center)) {
          b.drowned = true;
          b.drownedInAcid = center === Surface.Acid || center === Surface.AcidSwamp;
          b.settle = 0;
          events.push({ t: 'water', x: b.x, y: b.y, frame });
        } else {
          b.stoppedFrames++;
          stoppedCount++;
        }
      } else {
        b.stoppedFrames = 0;
      }
    }

    if (frame % SAMPLE_INTERVAL === 0) pushSample();

    if (stoppedCount >= balls.length && !anyMoving()) break;
  }

  // ensure final positions captured
  pushSample();

  const outcomes: BallOutcome[] = balls.map((b) => {
    const out: BallOutcome = {
      id: b.id,
      x: b.inHole ? cup.x : b.x,
      y: b.inHole ? cup.y : b.y,
      holed: b.holed || b.inHole,
      sankThisStroke: b.inHole && !b.holed,
      drowned: b.drowned,
    };
    if (b.drowned) {
      // Authentic resets: acid dissolves back to the tee; water returns the
      // ball to where this stroke began.
      if (b.drownedInAcid) {
        out.resetX = params.tee.x;
        out.resetY = params.tee.y;
      } else {
        out.resetX = b.startX;
        out.resetY = b.startY;
      }
    }
    return out;
  });

  return { ballIds, samples, outcomes, events, mutations, frames: frame };
}

function resolveBallCollision(a: BallRuntime, b: BallRuntime): void {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const d = Math.sqrt(dx * dx + dy * dy) || 1e-6;
  const nx = dx / d;
  const ny = dy / d;
  const va = a.vx * nx + a.vy * ny;
  const vb = b.vx * nx + b.vy * ny;
  if (vb - va > 0) return; // separating
  // equal-mass elastic: swap normal components, keep tangential
  const ta = -a.vx * ny + a.vy * nx;
  const tb = -b.vx * ny + b.vy * nx;
  a.vx = vb * nx - ta * ny;
  a.vy = vb * ny + ta * nx;
  b.vx = va * nx - tb * ny;
  b.vy = va * ny + tb * nx;
  // separate overlap
  const overlap = BALL_RADIUS * 2 - d;
  if (overlap > 0) {
    a.x -= nx * overlap * 0.5;
    a.y -= ny * overlap * 0.5;
    b.x += nx * overlap * 0.5;
    b.y += ny * overlap * 0.5;
  }
}

interface ReflectResult {
  hit: boolean;
  hard: boolean;
  bounciness: number;
}

type WallHitFn = (surf: Surface, sampleX: number, sampleY: number, impactVx: number, impactVy: number) => void;

/**
 * Reflect velocity off solid neighbors (cardinal + diagonal), authentic.
 * One-way walls only block motion opposing their arrow; every reflected
 * surface is reported (with the pre-reflection impact velocity) so
 * breakables/moveables can react.
 */
function reflectWalls(
  b: BallRuntime,
  cx: number,
  cy: number,
  nb: { n: Surface; e: Surface; s: Surface; w: Surface; ne: Surface; se: Surface; sw: Surface; nw: Surface },
  bounciness: number,
  onWallHit: WallHitFn,
): ReflectResult {
  const { vx: ivx, vy: ivy } = b;
  const solidN = isSolidFor(nb.n, ivx, ivy);
  const solidE = isSolidFor(nb.e, ivx, ivy);
  const solidS = isSolidFor(nb.s, ivx, ivy);
  const solidW = isSolidFor(nb.w, ivx, ivy);
  const solidNE = isSolidFor(nb.ne, ivx, ivy);
  const solidSE = isSolidFor(nb.se, ivx, ivy);
  const solidSW = isSolidFor(nb.sw, ivx, ivy);
  const solidNW = isSolidFor(nb.nw, ivx, ivy);

  const cardinal = solidN || solidE || solidS || solidW;
  let hit = false;
  let bn = bounciness;

  const eff = (surf: Surface) => {
    const sp = speedOf(b);
    const r = wallBounciness(surf, sp, bn);
    bn = r.nextBounciness;
    return r.factor;
  };

  if (!cardinal) {
    const { vx, vy } = b;
    if (solidNE && (vx > 0 && vy < 0 || (vx < 0 && vy < 0 && -vy > -vx) || (vx > 0 && vy > 0 && vx > vy))) {
      const f = eff(nb.ne);
      b.vx = vy * f;
      b.vy = vx * f;
      onWallHit(nb.ne, cx + CD, cy - CD, vx, vy);
      hit = true;
    }
    if (solidSE && (vx > 0 && vy > 0 || (vx > 0 && vy < 0 && vx > -vy) || (vx < 0 && vy > 0 && vy > -vx))) {
      const f = eff(nb.se);
      b.vx = -vy * f;
      b.vy = -vx * f;
      onWallHit(nb.se, cx + CD, cy + CD, vx, vy);
      hit = true;
    }
    if (solidSW && (vx < 0 && vy > 0 || (vx > 0 && vy > 0 && vy > vx) || (vx < 0 && vy < 0 && -vx > -vy))) {
      const f = eff(nb.sw);
      b.vx = vy * f;
      b.vy = vx * f;
      onWallHit(nb.sw, cx - CD, cy + CD, vx, vy);
      hit = true;
    }
    if (solidNW && (vx < 0 && vy < 0 || (vx < 0 && vy > 0 && -vx > vy) || (vx > 0 && vy < 0 && -vy > vx))) {
      const f = eff(nb.nw);
      b.vx = -vy * f;
      b.vy = -vx * f;
      onWallHit(nb.nw, cx - CD, cy - CD, vx, vy);
      hit = true;
    }
  } else {
    if (solidN && b.vy < 0) {
      const pvx = b.vx;
      const pvy = b.vy;
      const f = eff(nb.n);
      b.vx *= f;
      b.vy *= -f;
      onWallHit(nb.n, cx, cy - CC, pvx, pvy);
      hit = true;
    } else if (solidS && b.vy > 0) {
      const pvx = b.vx;
      const pvy = b.vy;
      const f = eff(nb.s);
      b.vx *= f;
      b.vy *= -f;
      onWallHit(nb.s, cx, cy + CC, pvx, pvy);
      hit = true;
    }
    if (solidE && b.vx > 0) {
      const pvx = b.vx;
      const pvy = b.vy;
      const f = eff(nb.e);
      b.vx *= -f;
      b.vy *= f;
      onWallHit(nb.e, cx + CC, cy, pvx, pvy);
      return { hit: true, hard: true, bounciness: bn };
    }
    if (solidW && b.vx < 0) {
      const pvx = b.vx;
      const pvy = b.vy;
      const f = eff(nb.w);
      b.vx *= -f;
      b.vy *= f;
      onWallHit(nb.w, cx - CC, cy, pvx, pvy);
      return { hit: true, hard: true, bounciness: bn };
    }
  }

  return { hit, hard: hit, bounciness: bn };
}
